import React, { Component, ReactNode } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from '../../redux/store';
import { setAttribute } from '../../redux/actions/UserActions';
import { TabOrderItem } from 'domain/rbac/RbacDomains';

class CustomTabv3 extends Component<Props & ReduxProps, State> {
  public state = new State();

  public render() {
    const { onClick } = this.props;
    const { orderedTabs, draggedIndex, dropTargetIndex, isDragging } = this.state;
    const selectedTab = this.props.selectedTab || this.state.selectedTab;
    const tabsToRender = orderedTabs.length > 0 ? orderedTabs : this.props.tab;

    return (
      <div className={`custom-scroll flex h-[38px] max-w-full justify-center items-center overflow-y-hidden select-none`}>
        <div className={'flex h-full w-full justify-start items-center'}>
          {tabsToRender?.map((t, i) => {
            const isSelected = selectedTab == t.key;
            const isDragged = draggedIndex === i;
            const showLeftIndicator = isDragging && dropTargetIndex === i && draggedIndex !== null && draggedIndex > i;
            const showRightIndicator = isDragging && dropTargetIndex === i && draggedIndex !== null && draggedIndex < i;

            return (
              <div
                key={t.key}
                draggable={!t.disabled}
                onDragStart={(e) => this.onDragStart(e, i)}
                onDragOver={(e) => this.onDragOver(e, i)}
                onDragEnd={this.onDragEnd}
                data-testid={t.testid}
                onClick={(e: any) => {
                  if (!t.disabled) {
                    this.setState({ selectedTab: t.key }, () => onClick?.(e, t.key));
                  }
                }}
                className={`
                  relative
                  flex h-full items-center justify-start px-5 text-lg whitespace-nowrap transition-all duration-200 ease-in-out
                  border-b-[2px]
                  ${t.disabled ? 'cursor-not-allowed opacity-40' : 'cursor-grab active:cursor-grabbing'}
                  ${isSelected ? 'border-primary text-primary font-medium opacity-100' : 'border-[#DDDDDD] text-f-black-2 font-normal opacity-70 hover:opacity-100 hover:bg-gray-50'}
                  ${isDragged ? 'bg-gray-100 opacity-50 z-10' : ''}
                  ${showLeftIndicator ? 'before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-primary' : ''}
                  ${showRightIndicator ? 'after:absolute after:right-0 after:top-0 after:h-full after:w-[3px] after:bg-primary' : ''}
                `}
                style={{
                  transform: isDragged ? 'scale(1.02)' : 'scale(1)',
                }}
              >
                {t.label}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  private onDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    this.setState({
      draggedIndex: index,
      dropTargetIndex: index,
      isDragging: true,
    });
    e.dataTransfer.effectAllowed = 'move';
  };

  private onDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const { draggedIndex, dropTargetIndex } = this.state;

    if (draggedIndex === null) return;

    if (dropTargetIndex !== index) {
      this.setState({ dropTargetIndex: index });
    }
  };

  private onDragEnd = () => {
    const { draggedIndex, dropTargetIndex, orderedTabs } = this.state;
    const { onOrderChange, attributes, setAttribute } = this.props;

    if (draggedIndex !== null && dropTargetIndex !== null && draggedIndex !== dropTargetIndex) {
      const newOrderedTabs = [...orderedTabs];
      const draggedItem = newOrderedTabs[draggedIndex];
      newOrderedTabs.splice(draggedIndex, 1);
      newOrderedTabs.splice(dropTargetIndex, 0, draggedItem);

      this.setState(
        {
          orderedTabs: newOrderedTabs,
          draggedIndex: null,
          dropTargetIndex: null,
          isDragging: false,
        },
        () => {
          if (onOrderChange) {
            onOrderChange(newOrderedTabs.map((t) => t.key));
          }

          setAttribute({
            ...attributes,
            tabOrders: newOrderedTabs.map((t) => ({
              key: t.key,
              label: typeof t.label === 'string' ? t.label : '',
              disabled: t.disabled,
              testid: t.testid,
            })),
          });
        },
      );
    } else {
      this.setState({
        draggedIndex: null,
        dropTargetIndex: null,
        isDragging: false,
      });
    }
  };

  public componentDidMount() {
    const { tab, selectedTab, attributes } = this.props;
    if (tab && tab.length > 0) {
      let orderedTabs = tab;

      if (attributes?.tabOrders && attributes.tabOrders.length > 0) {
        const savedKeys: string[] = attributes.tabOrders.map((t: TabOrderItem) => t.key);
        const sortedTabs = savedKeys.map((key: string) => tab.find((t) => t.key === key)).filter((t): t is CustomTabItemProps => t !== undefined);
        const newTabs = tab.filter((t) => !savedKeys.includes(t.key));
        orderedTabs = [...sortedTabs, ...newTabs];
      }

      this.setState({
        selectedTab: selectedTab || orderedTabs[0]?.key,
        orderedTabs,
      });
    }
  }

  public componentDidUpdate(prevProps: Props & ReduxProps) {
    const { tab, attributes } = this.props;

    if (tab !== prevProps.tab || attributes?.tabOrders !== prevProps.attributes?.tabOrders) {
      const newKeys = tab.map((t) => t.key);
      const existingKeys = this.state.orderedTabs.map((t) => t.key);
      const keysAreSame = newKeys.length === existingKeys.length && newKeys.every((key) => existingKeys.includes(key));

      if (!keysAreSame) {
        if (attributes?.tabOrders && attributes.tabOrders.length > 0) {
          const savedKeys: string[] = attributes.tabOrders.map((t: TabOrderItem) => t.key);
          const sortedTabs = savedKeys.map((key: string) => tab.find((t) => t.key === key)).filter((t): t is CustomTabItemProps => t !== undefined);
          const newTabs = tab.filter((t) => !savedKeys.includes(t.key));
          this.setState({ orderedTabs: [...sortedTabs, ...newTabs] });
        } else {
          this.setState({ orderedTabs: tab });
        }
      } else {
        const updatedOrderedTabs = this.state.orderedTabs.map((orderedTab) => {
          const updatedTab = tab.find((t) => t.key === orderedTab.key);
          return updatedTab || orderedTab;
        });
        this.setState({ orderedTabs: updatedOrderedTabs });
      }
    }
  }
}

interface Props {
  tab: Array<CustomTabItemProps>;
  onClick?: (e: any, tab: string) => void;
  router?: boolean;
  selectedTab?: string;
  onOrderChange?: (orderedKeys: string[]) => void;
}

export interface CustomTabItemProps {
  key: string;
  label: string | ReactNode;
  disabled?: boolean;
  content?: ReactNode | any;
  testid?: string;
}

class State {
  selectedTab: string | undefined;
  orderedTabs: Array<CustomTabItemProps> = [];
  draggedIndex: number | null = null;
  dropTargetIndex: number | null = null;
  isDragging: boolean = false;
}

const mapStateToProps = (state: RootState) => ({
  attributes: state.auth.attributes,
});

const connector = connect(mapStateToProps, { setAttribute });
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CustomTabv3);
